import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { GLASS_SECONDARY_TEXT_CLASS, compressStateChips, continuityStatusTone, productTone, readNumberMeta, readProjectionCouplingLabel, readStringMeta, visibleLaneItems } from "./rendering";

type ContinuityCluster = {
  startPx: number;
  widthPx: number;
  itemCount: number;
  severity: "info" | "warning" | "critical";
  status: string;
  warning: string | null;
};

function severityRank(severity: ContinuityCluster["severity"]) {
  switch (severity) {
    case "critical":
      return 3;
    case "warning":
      return 2;
    default:
      return 1;
  }
}

function stateChip(label: string) {
  return <span className="rounded-full border border-amber-300/18 bg-amber-300/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-amber-50/86">{label}</span>;
}

function readProjectionCounts(lane: LaneRendererProps["lane"]) {
  const projectionHealth = lane.metadata?.projectionHealth;
  const byStatus = projectionHealth && typeof projectionHealth === "object" && "byStatus" in projectionHealth && projectionHealth.byStatus && typeof projectionHealth.byStatus === "object"
    ? projectionHealth.byStatus as Record<string, unknown>
    : {};

  const readCount = (key: string) => {
    const value = byStatus[key];
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
  };

  return {
    stale: readCount("stale"),
    invalid: readCount("invalid"),
    reviewRequired: readCount("review_required"),
  };
}

function awaitingLabel(diagnosticCount: number, items: LaneRendererProps["items"], visibleItemCount: number) {
  if (items.length > 0 && visibleItemCount === 0) return "outside window";
  if (diagnosticCount > 0) return "awaiting continuity projection";
  return "awaiting diagnostics";
}

function buildContinuityClusters(items: LaneRendererProps["items"], viewport: LaneRendererProps["viewport"]): ContinuityCluster[] {
  const visible = visibleLaneItems(items, viewport);
  if (!visible.length) return [];

  const sorted = [...visible].sort((left, right) => left.leftPx - right.leftPx);
  const clusters: ContinuityCluster[] = [];

  for (const visibleItem of sorted) {
    const item = visibleItem.item;
    const severity = (("severity" in item ? item.severity : undefined) ?? "info") as ContinuityCluster["severity"];
    const status = "overlayKey" in item ? item.overlayKey : item.id;
    const warning = readStringMeta(item, "warning");
    const current: ContinuityCluster = {
      startPx: visibleItem.leftPx,
      widthPx: visibleItem.widthPx,
      itemCount: 1,
      severity,
      status,
      warning,
    };

    const previous = clusters[clusters.length - 1];
    if (!previous) {
      clusters.push(current);
      continue;
    }

    const previousEnd = previous.startPx + previous.widthPx;
    if (visibleItem.leftPx <= previousEnd + 12) {
      const nextEnd = Math.max(previousEnd, visibleItem.leftPx + visibleItem.widthPx);
      previous.widthPx = nextEnd - previous.startPx;
      previous.itemCount += 1;
      if (severityRank(severity) >= severityRank(previous.severity)) {
        previous.severity = severity;
        previous.status = status;
        previous.warning = warning;
      }
      continue;
    }

    clusters.push(current);
  }

  return clusters;
}

export default function ProjectionContinuityLaneRenderer({ lane, items, viewport, syncBadge }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const clusters = buildContinuityClusters(items, viewport);
  const diagnosticCount = readNumberMeta({ ...lane, metadata: lane.metadata } as never, "diagnosticCount") ?? 0;
  const warningCount = readNumberMeta({ ...lane, metadata: lane.metadata } as never, "warningCount") ?? 0;
  const anchoredRangeLabel = readStringMeta({ ...lane, metadata: lane.metadata } as never, "anchoredRangeLabel");
  const projectionCounts = readProjectionCounts(lane);
  const emptyState = awaitingLabel(diagnosticCount, items, clusters.length);
  const laneStateChips = [
    projectionCounts.stale > 0 ? `${projectionCounts.stale} stale` : null,
    projectionCounts.invalid > 0 ? `${projectionCounts.invalid} invalid` : null,
    projectionCounts.reviewRequired > 0 ? `${projectionCounts.reviewRequired} review` : null,
    warningCount > 0 ? `${warningCount} warnings` : null,
    anchoredRangeLabel,
  ].filter((value): value is string => Boolean(value));
  const primaryCouplingLabel = readProjectionCouplingLabel(lane.metadata ?? null, lane.id, viewport.lodTier);
  const compressedLaneState = compressStateChips(laneStateChips, viewport.lodTier);
  const showBodyReason = viewport.lodTier !== "far";

  return (
    <div className="relative h-full min-h-[48px]" data-lane-product="projection_continuity" data-lane-id={lane.id}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-white/8" />
      {clusters.length > 0 ? (
        <>
          {clusters.map((cluster, index) => {
          const clusterTone = continuityStatusTone(cluster.status, cluster.severity);
          const isDense = cluster.itemCount > 1 || cluster.widthPx < 120;
          const isFar = viewport.lodTier === "far";
          const isMid = viewport.lodTier === "mid";

          return (
            <div
              key={`${cluster.status}-${index}`}
              data-visual-lane-item-id={`continuity-cluster-${index}`}
              className={["absolute top-1/2 h-10 -translate-y-1/2 overflow-hidden rounded-xl border px-3 shadow-[0_8px_20px_rgba(2,6,23,0.16)]", clusterTone].join(" ")}
              style={{ left: cluster.startPx, width: cluster.widthPx }}
              title={cluster.warning ?? cluster.status}
            >
              <div className="flex h-full items-center justify-between gap-2">
                <div className="min-w-0">
                  {isFar ? (
                    <div className="text-[8px] font-semibold uppercase tracking-[0.14em] text-current/84">CTY</div>
                  ) : (
                    <>
                      <div className="truncate text-[11px] font-semibold capitalize">{cluster.status.replace(/_/g, " ")}</div>
                      {!isMid && cluster.warning ? <div className="mt-0.5 truncate text-[8px] font-semibold uppercase tracking-[0.12em] text-current/78">{cluster.warning}</div> : null}
                    </>
                  )}
                </div>
                {isDense ? (
                  <div className="rounded-full border border-current/16 bg-black/10 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-current/84">
                    {cluster.itemCount}x
                  </div>
                ) : null}
              </div>
            </div>
          );
          })}
        </>
      ) : (
        <div className={["min-h-[56px] rounded-2xl border px-3 py-3", tone.emptyClassName].join(" ")}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                <span>
                  {items.length > 0
                    ? "Continuity overlays are outside the active window."
                    : diagnosticCount > 0
                      ? "Continuity overlays are waiting on derived projection spans."
                      : "Continuity diagnostics are waiting on source motion."}
                </span>
                {stateChip(emptyState)}
                {compressedLaneState.visible.map((label) => (
                  <React.Fragment key={label}>{stateChip(label)}</React.Fragment>
                ))}
                {compressedLaneState.hiddenCount > 0 ? stateChip(`+${compressedLaneState.hiddenCount} more`) : null}
                {primaryCouplingLabel ? stateChip(primaryCouplingLabel) : null}
              </div>
              {showBodyReason ? (
              <div className={["mt-1 text-[10px]", GLASS_SECONDARY_TEXT_CLASS].join(" ")}>
                {syncBadge?.reason ?? "Continuity checks appear here once authored arc or FX transitions create derived diagnostics."}
              </div>
              ) : null}
            </div>
            <div className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-300/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-rose-300/80" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}